import type { ClientConfigRow, ConfigDefinition } from '@inboundx/shared/config';
import { applyMergeStrategy } from '@inboundx/shared/config';
import type { Workspace } from '@inboundx/shared/platform';
import { computeOverride } from './configDiff';

/**
 * Configuration of a workspace placed under a path (`example.com/fr`).
 *
 * Such a workspace inherits the configuration of the workspace that owns its host and
 * stores only what differs, under its own config key. The chat runtime and the widget
 * resolve it with `config.workspace_config_rows` (IX-4525); these two functions are the
 * backoffice's read and write halves of that same rule.
 */

type RowKey = Pick<ClientConfigRow, 'config_slug' | 'config_scope'>;

function rowKey(row: RowKey): string {
    return `${row.config_slug}:${row.config_scope}`;
}

function asObject(value: unknown): Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : {};
}

/** The rows a path-rooted workspace resolves: its host's, with its own laid over. */
export function mergeWorkspaceConfigRows(
    hostRows: ClientConfigRow[],
    ownRows: ClientConfigRow[],
    definitions: ConfigDefinition[],
    configKey: string
): ClientConfigRow[] {
    const strategies = new Map(
        definitions.map((d) => [
            rowKey({ config_slug: d.slug, config_scope: d.scope }),
            d.merge_strategy,
        ])
    );
    const own = new Map(ownRows.map((row) => [rowKey(row), row]));
    const merged: ClientConfigRow[] = hostRows.map((host) => {
        const override = own.get(rowKey(host));
        own.delete(rowKey(host));
        if (!override) return { ...host, domain: configKey };
        return {
            ...host,
            domain: configKey,
            enabled: override.enabled ?? host.enabled,
            config:
                override.config === null
                    ? host.config
                    : (applyMergeStrategy(
                          strategies.get(rowKey(host)) ?? 'deep_merge',
                          asObject(host.config),
                          asObject(override.config)
                      ) as ClientConfigRow['config']),
        };
    });
    return [...merged, ...own.values()];
}

/** The feature switch a path workspace inherits: its host's stored one, else the default. */
export function inheritedEnabled(
    definition: Pick<ConfigDefinition, 'enabled_by_default'> | undefined,
    hostRow: Pick<ClientConfigRow, 'enabled'> | undefined
): boolean {
    return hostRow?.enabled ?? definition?.enabled_by_default ?? true;
}

interface SubmittedSection {
    enabled?: boolean | null;
    /** What a screen saves: the form's values minus the schema defaults. */
    config: Record<string, unknown> | null;
}

/**
 * The smallest row that, laid over the host's, resolves to what the screen submitted.
 *
 * Storing the submitted override as is would freeze a copy of every value the host
 * customised, and later edits on the host would stop reaching this workspace. It would
 * also lose a value set back to the schema default, which the host's row would
 * re-override. So diff against what the workspace inherits, not against the defaults.
 *
 * ponytail: a key the host sets inside a deep-merged object can be replaced here but not
 * removed — same ceiling as `merge_workspace_json`. Needs an explicit tombstone in both
 * if a screen ever has to unset an inherited map entry.
 */
export function workspaceOverride(
    definition: Pick<ConfigDefinition, 'merge_strategy' | 'default_config' | 'enabled_by_default'>,
    schemaDefaults: Record<string, unknown>,
    hostRow: Pick<ClientConfigRow, 'enabled' | 'config'> | undefined,
    submitted: SubmittedSection
): { enabled: boolean | null; config: Record<string, unknown> | null } {
    const enabled =
        submitted.enabled === undefined ||
        submitted.enabled === null ||
        submitted.enabled === inheritedEnabled(definition, hostRow)
            ? null
            : submitted.enabled;

    const hostConfig = hostRow?.config ? asObject(hostRow.config) : null;
    if (definition.merge_strategy === 'full_override') {
        const same = JSON.stringify(submitted.config) === JSON.stringify(hostConfig);
        // A null config means "inherit the host" to the runtime. A section brought back
        // to its defaults (every CTA removed) submits null, so store an empty object:
        // it replaces the host's row instead of silently keeping it.
        return { enabled, config: same ? null : (submitted.config ?? {}) };
    }

    const resolve = (override: Record<string, unknown> | null) =>
        applyMergeStrategy(
            'deep_merge',
            schemaDefaults,
            applyMergeStrategy('deep_merge', asObject(definition.default_config), override)
        );
    return { enabled, config: computeOverride(resolve(submitted.config), resolve(hostConfig)) };
}

/** The workspaces that inherit `host`'s configuration: placed under a path of its host. */
export function inheritingWorkspaces(
    host: Pick<Workspace, 'domain' | 'configKey'>,
    workspaces: Workspace[]
): Workspace[] {
    if (host.configKey !== host.domain) return [];
    return workspaces.filter((w) => w.domain === host.domain && w.configKey !== host.domain);
}

/**
 * Whether a workspace's own row departs from its host for this section. Any stored
 * value counts: an array or a field set here no longer follows the host's edits.
 */
export function customizesSection(
    ownRows: Pick<ClientConfigRow, 'config_slug' | 'config_scope' | 'enabled' | 'config'>[],
    slug: string,
    scope: string
): boolean {
    const own = ownRows.find((r) => r.config_slug === slug && r.config_scope === scope);
    return own !== undefined && (own.enabled !== null || own.config !== null);
}
